import { useState, useRef, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  /** Typography variant for display mode */
  variant?: 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption';
  fontWeight?: number;
}

export default function InlineEditField({
  value,
  onChange,
  placeholder = '',
  multiline = false,
  rows = 2,
  variant = 'subtitle2',
  fontWeight,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed !== value) {
      onChange(trimmed);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      commit();
    }
    if (e.key === 'Escape') {
      setDraft(value);
      setEditing(false);
    }
  }

  if (editing) {
    return (
      <TextField
        inputRef={inputRef}
        size="small"
        fullWidth
        multiline={multiline}
        rows={multiline ? rows : undefined}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        sx={{ '& .MuiInputBase-root': { py: multiline ? 0.5 : 0 } }}
      />
    );
  }

  return (
    <Box
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      sx={{ cursor: 'pointer', minHeight: 24, display: 'flex', alignItems: 'center' }}
    >
      <Typography
        variant={variant}
        sx={{
          fontWeight,
          color: value ? 'text.primary' : 'text.disabled',
          '&:hover': { color: value ? 'text.primary' : 'text.secondary' },
        }}
        noWrap={!multiline}
      >
        {value || placeholder}
      </Typography>
    </Box>
  );
}
